import {
    ChevronDown,
    ChevronUp,
    FileText,
    GalleryHorizontal,
    Image,
    Link2,
    Plus,
    Trash2,
} from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useRestClient } from "../../../core/api/RestClientContext";
import {
    NotificationRequestV2,
    NotificationUpload,
    TelegramMediaGroupItem,
    TelegramNotificationBlock,
    TelegramNotificationBlockType,
    TelegramNotificationButton,
    TelegramNotificationButtonRow,
    TelegramNotificationPayload,
    TelegramParseMode,
} from "../../../core/models/notification";
import { UsersFilterShape } from "../../../core/models/user";
import { Button } from "../../../core/components/Button";

interface TelegramNotificationComposerProps {
    filter?: UsersFilterShape;
}

type MediaItemType = "PHOTO" | "DOCUMENT";

interface ComposerFileRef {
    key: string;
    file: File;
}

type ButtonActionKind = "URL" | "CALLBACK";

interface ComposerButton extends TelegramNotificationButton {
    id: string;
    actionKind: ButtonActionKind;
}

interface ComposerButtonRow extends TelegramNotificationButtonRow {
    id: string;
    buttons: ComposerButton[];
}

interface ComposerMediaGroupItem extends TelegramMediaGroupItem {
    id: string;
    fileRef?: ComposerFileRef;
}

interface ComposerBlock {
    id: string;
    type: TelegramNotificationBlockType;
    text: string;
    caption: string;
    buttons: ComposerButtonRow[];
    fileRef?: ComposerFileRef;
    items: ComposerMediaGroupItem[];
}

const emptyButton = (): ComposerButton => ({
    id: crypto.randomUUID(),
    text: "",
    url: "",
    callbackData: "",
    actionKind: "URL",
});

const emptyButtonRow = (): ComposerButtonRow => ({
    id: crypto.randomUUID(),
    buttons: [emptyButton()],
});

const emptyTextBlock = (): ComposerBlock => ({
    id: crypto.randomUUID(),
    type: "TEXT",
    text: "",
    caption: "",
    buttons: [],
    items: [],
});

const createSingleMediaBlock = (type: "PHOTO" | "DOCUMENT"): ComposerBlock => ({
    id: crypto.randomUUID(),
    type,
    text: "",
    caption: "",
    buttons: [],
    items: [],
});

const mediaTypeFromFile = (file: File): MediaItemType => file.type.startsWith("image/") ? "PHOTO" : "DOCUMENT";

const toPayloadButton = (button: ComposerButton): TelegramNotificationButton =>
    button.actionKind === "URL"
        ? { text: button.text.trim(), url: button.url?.trim() }
        : { text: button.text.trim(), callbackData: button.callbackData?.trim() };

const isValidButton = (button: ComposerButton): boolean => {
    if (!button.text.trim()) {
        return false;
    }
    return button.actionKind === "URL"
        ? Boolean(button.url?.trim())
        : Boolean(button.callbackData?.trim());
};

const isValidButtonRow = (row: ComposerButtonRow): boolean => row.buttons.some(isValidButton);

const isValidBlock = (block: ComposerBlock): boolean => {
    switch (block.type) {
        case "TEXT":
            return Boolean(block.text.trim());
        case "PHOTO":
        case "DOCUMENT":
            return Boolean(block.fileRef);
        case "MEDIA_GROUP":
            return block.items.some((item) => item.fileRef);
        default:
            return false;
    }
};

export const TelegramNotificationComposer: React.FC<TelegramNotificationComposerProps> = ({ filter }) => {
    const restClient = useRestClient();
    const blockFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const mediaGroupFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const [parseMode, setParseMode] = useState<TelegramParseMode>("NONE");
    const [disableWebPagePreview, setDisableWebPagePreview] = useState(false);
    const [sendToAll, setSendToAll] = useState(false);
    const [blocks, setBlocks] = useState<ComposerBlock[]>([emptyTextBlock()]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const updateBlock = useCallback((blockId: string, updater: (block: ComposerBlock) => ComposerBlock) => {
        setBlocks((prev) => prev.map((block) => block.id === blockId ? updater(block) : block));
    }, []);

    const addBlock = useCallback((type: TelegramNotificationBlockType) => {
        setBlocks((prev) => [
            ...prev,
            type === "TEXT"
                ? emptyTextBlock()
                : type === "MEDIA_GROUP"
                    ? {
                        id: crypto.randomUUID(),
                        type,
                        text: "",
                        caption: "",
                        buttons: [],
                        items: [],
                    }
                    : createSingleMediaBlock(type),
        ]);
    }, []);

    const removeBlock = useCallback((blockId: string) => {
        setBlocks((prev) => prev.filter((block) => block.id !== blockId));
    }, []);

    const moveBlock = useCallback((blockId: string, direction: -1 | 1) => {
        setBlocks((prev) => {
            const index = prev.findIndex((block) => block.id === blockId);
            const targetIndex = index + direction;
            if (index < 0 || targetIndex < 0 || targetIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });
    }, []);

    const selectSingleFile = (blockId: string, file?: File | null) => {
        if (!file) {
            return;
        }
        updateBlock(blockId, (block) => ({
            ...block,
            fileRef: {
                key: crypto.randomUUID(),
                file,
            },
        }));
    };

    const addMediaGroupFiles = (blockId: string, files: FileList | null) => {
        const selected = Array.from(files || []);
        if (selected.length === 0) {
            return;
        }

        updateBlock(blockId, (block) => ({
            ...block,
            items: [
                ...block.items,
                ...selected.map((file) => ({
                    id: crypto.randomUUID(),
                    type: mediaTypeFromFile(file),
                    uploadKey: crypto.randomUUID(),
                    caption: "",
                    fileRef: {
                        key: crypto.randomUUID(),
                        file,
                    },
                })),
            ],
        }));
    };

    const updateMediaGroupItem = (
        blockId: string,
        itemId: string,
        updater: (item: ComposerMediaGroupItem) => ComposerMediaGroupItem,
    ) => {
        updateBlock(blockId, (block) => ({
            ...block,
            items: block.items.map((item) => item.id === itemId ? updater(item) : item),
        }));
    };

    const removeMediaGroupItem = (blockId: string, itemId: string) => {
        updateBlock(blockId, (block) => ({
            ...block,
            items: block.items.filter((item) => item.id !== itemId),
        }));
    };

    const addButtonRow = (blockId: string) => {
        updateBlock(blockId, (block) => ({
            ...block,
            buttons: [...block.buttons, emptyButtonRow()],
        }));
    };

    const removeButtonRow = (blockId: string, rowId: string) => {
        updateBlock(blockId, (block) => ({
            ...block,
            buttons: block.buttons.filter((row) => row.id !== rowId),
        }));
    };

    const addButton = (blockId: string, rowId: string) => {
        updateBlock(blockId, (block) => ({
            ...block,
            buttons: block.buttons.map((row) => row.id === rowId
                ? { ...row, buttons: [...row.buttons, emptyButton()] }
                : row),
        }));
    };

    const updateButton = (
        blockId: string,
        rowId: string,
        buttonId: string,
        patch: Partial<ComposerButton>,
    ) => {
        updateBlock(blockId, (block) => ({
            ...block,
            buttons: block.buttons.map((row) => row.id === rowId
                ? {
                    ...row,
                    buttons: row.buttons.map((button) => button.id === buttonId
                        ? { ...button, ...patch }
                        : button),
                }
                : row),
        }));
    };

    const removeButton = (blockId: string, rowId: string, buttonId: string) => {
        updateBlock(blockId, (block) => ({
            ...block,
            buttons: block.buttons.map((row) => row.id === rowId
                ? { ...row, buttons: row.buttons.filter((button) => button.id !== buttonId) }
                : row),
        }));
    };

    const buildRequest = (): { request: NotificationRequestV2; files: File[] } => {
        const uploads: NotificationUpload[] = [];
        const files: File[] = [];
        const seen = new Set<string>();

        const registerUpload = (fileRef?: ComposerFileRef): string | undefined => {
            if (!fileRef) {
                return undefined;
            }
            if (!seen.has(fileRef.key)) {
                seen.add(fileRef.key);
                uploads.push({
                    key: fileRef.key,
                    fileName: fileRef.file.name,
                });
                files.push(fileRef.file);
            }
            return fileRef.key;
        };

        const payloadBlocks = blocks
            .filter(isValidBlock)
            .map<TelegramNotificationBlock>((block) => {
                if (block.type === "TEXT") {
                    return {
                        type: "TEXT",
                        text: block.text.trim(),
                        buttonRows: block.buttons.filter(isValidButtonRow).map((row) => ({
                            buttons: row.buttons.filter(isValidButton).map(toPayloadButton),
                        })),
                    };
                }

                if (block.type === "MEDIA_GROUP") {
                    return {
                        type: "MEDIA_GROUP",
                        items: block.items
                            .filter((item) => item.fileRef)
                            .map((item) => ({
                                type: item.type,
                                uploadKey: registerUpload(item.fileRef)!,
                                caption: item.caption.trim() || undefined,
                            })),
                    };
                }

                return {
                    type: block.type,
                    caption: block.caption.trim() || undefined,
                    uploadKey: registerUpload(block.fileRef),
                    buttonRows: block.buttons.filter(isValidButtonRow).map((row) => ({
                        buttons: row.buttons.filter(isValidButton).map(toPayloadButton),
                    })),
                };
            });

        const telegram: TelegramNotificationPayload = {
            parseMode,
            disableWebPagePreview,
            blocks: payloadBlocks,
        };

        return {
            request: {
                id: crypto.randomUUID(),
                channel: "TELEGRAM",
                sendToAll,
                filter,
                telegram,
                uploads,
            },
            files,
        };
    };

    const canSubmit = blocks.some(isValidBlock);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            const { request, files } = buildRequest();
            const response = await restClient.sendNotificationV2(request, files);

            if (response.success) {
                setSuccess(`Уведомления поставлены в очередь: ${response.total}`);
                setBlocks([emptyTextBlock()]);
                setDisableWebPagePreview(false);
                setParseMode("NONE");
                setSendToAll(false);
            } else {
                setError(`Ошибка отправки уведомлений: ${response.message || ""} ${response.failedUserIds?.join(", ") || ""}`);
            }
        } catch (err) {
            setError("Ошибка отправки уведомлений: " + err);
            console.error("Ошибка отправки уведомлений:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="notification-form">
            <div className="notification-toolbar">
                <div className="notification-toolbar-row">
                    <label className="form-inline-label">
                        Parse mode
                        <select
                            value={parseMode}
                            onChange={(e) => setParseMode(e.target.value as TelegramParseMode)}
                            disabled={loading}
                        >
                            <option value="NONE">None</option>
                            <option value="MARKDOWN_V2">MarkdownV2</option>
                            <option value="HTML">HTML</option>
                        </select>
                    </label>
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={disableWebPagePreview}
                            onChange={(e) => setDisableWebPagePreview(e.target.checked)}
                            disabled={loading}
                        />
                        Отключить preview ссылок
                    </label>
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={sendToAll}
                            onChange={(e) => setSendToAll(e.target.checked)}
                            disabled={loading}
                        />
                        Отправить всем пользователям
                    </label>
                </div>
            </div>

            <div className="notification-block-actions">
                <Button type="button" variant="secondary" onClick={() => addBlock("TEXT")} icon={<Plus size={14} />}>
                    Текст
                </Button>
                <Button type="button" variant="secondary" onClick={() => addBlock("PHOTO")} icon={<Image size={14} />}>
                    Фото
                </Button>
                <Button type="button" variant="secondary" onClick={() => addBlock("DOCUMENT")} icon={<FileText size={14} />}>
                    Документ
                </Button>
                <Button type="button" variant="secondary" onClick={() => addBlock("MEDIA_GROUP")} icon={<GalleryHorizontal size={14} />}>
                    Медиа-группа
                </Button>
            </div>

            <div className="notification-block-list">
                {blocks.map((block, index) => (
                    <div key={block.id} className="notification-block-card">
                        <div className="notification-block-header">
                            <div>
                                <strong>{index + 1}. {block.type}</strong>
                            </div>
                            <div className="notification-block-controls">
                                <Button type="button" variant="ghost" icon={<ChevronUp size={14} />} iconOnly onClick={() => moveBlock(block.id, -1)} disabled={loading || index === 0} />
                                <Button type="button" variant="ghost" icon={<ChevronDown size={14} />} iconOnly onClick={() => moveBlock(block.id, 1)} disabled={loading || index === blocks.length - 1} />
                                <Button type="button" variant="ghost" icon={<Trash2 size={14} />} iconOnly onClick={() => removeBlock(block.id)} disabled={loading || blocks.length === 1} />
                            </div>
                        </div>

                        {block.type === "TEXT" && (
                            <div className="form-group">
                                <label>Текст сообщения</label>
                                <textarea
                                    value={block.text}
                                    onChange={(e) => updateBlock(block.id, (current) => ({ ...current, text: e.target.value }))}
                                    rows={5}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        {(block.type === "PHOTO" || block.type === "DOCUMENT") && (
                            <>
                                <div className="form-group">
                                    <label>{block.type === "PHOTO" ? "Файл изображения" : "Файл документа"}</label>
                                    <input
                                        ref={(node) => {
                                            blockFileInputRefs.current[block.id] = node;
                                        }}
                                        type="file"
                                        className="file-input"
                                        onChange={(e) => {
                                            selectSingleFile(block.id, e.target.files?.[0]);
                                            if (e.target) {
                                                e.target.value = "";
                                            }
                                        }}
                                        disabled={loading}
                                    />
                                    <div className="file-input-container">
                                        <Button type="button" variant="secondary" onClick={() => blockFileInputRefs.current[block.id]?.click()} disabled={loading}>
                                            Выбрать файл
                                        </Button>
                                        {block.fileRef && (
                                            <span className="inline-file-name">{block.fileRef.file.name}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Caption</label>
                                    <textarea
                                        value={block.caption}
                                        onChange={(e) => updateBlock(block.id, (current) => ({ ...current, caption: e.target.value }))}
                                        rows={4}
                                        disabled={loading}
                                    />
                                </div>
                            </>
                        )}

                        {block.type === "MEDIA_GROUP" && (
                            <>
                                <div className="form-group">
                                    <label>Файлы для media group</label>
                                    <input
                                        ref={(node) => {
                                            mediaGroupFileInputRefs.current[block.id] = node;
                                        }}
                                        type="file"
                                        className="file-input"
                                        multiple
                                        onChange={(e) => {
                                            addMediaGroupFiles(block.id, e.target.files);
                                            if (e.target) {
                                                e.target.value = "";
                                            }
                                        }}
                                        disabled={loading}
                                    />
                                    <div className="file-input-container">
                                        <Button type="button" variant="secondary" onClick={() => mediaGroupFileInputRefs.current[block.id]?.click()} disabled={loading}>
                                            Добавить файлы
                                        </Button>
                                    </div>
                                </div>

                                <div className="attached-files">
                                    {block.items.map((item) => (
                                        <div key={item.id} className="notification-item-card">
                                            <div className="notification-item-header">
                                                <strong>{item.fileRef?.file.name || "Файл"}</strong>
                                                <Button type="button" variant="ghost" icon={<Trash2 size={14} />} iconOnly onClick={() => removeMediaGroupItem(block.id, item.id)} disabled={loading} />
                                            </div>
                                            <label className="form-inline-label">
                                                Тип
                                                <select
                                                    value={item.type}
                                                    onChange={(e) => updateMediaGroupItem(block.id, item.id, (current) => ({
                                                        ...current,
                                                        type: e.target.value as MediaItemType,
                                                    }))}
                                                    disabled={loading}
                                                >
                                                    <option value="PHOTO">PHOTO</option>
                                                    <option value="DOCUMENT">DOCUMENT</option>
                                                </select>
                                            </label>
                                            <label>Caption</label>
                                            <textarea
                                                value={item.caption || ""}
                                                onChange={(e) => updateMediaGroupItem(block.id, item.id, (current) => ({
                                                    ...current,
                                                    caption: e.target.value,
                                                }))}
                                                rows={3}
                                                disabled={loading}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {block.type !== "MEDIA_GROUP" && (
                            <div className="notification-buttons-editor">
                                <div className="notification-buttons-header">
                                    <strong>Inline buttons</strong>
                                    <Button type="button" variant="secondary" onClick={() => addButtonRow(block.id)} disabled={loading} icon={<Link2 size={14} />}>
                                        Добавить ряд кнопок
                                    </Button>
                                </div>

                                {block.buttons.map((row) => (
                                    <div key={row.id} className="notification-button-row">
                                        <div className="notification-button-row-header">
                                            <span>Ряд кнопок</span>
                                            <div className="notification-block-controls">
                                                <Button type="button" variant="ghost" icon={<Plus size={14} />} iconOnly onClick={() => addButton(block.id, row.id)} disabled={loading} />
                                                <Button type="button" variant="ghost" icon={<Trash2 size={14} />} iconOnly onClick={() => removeButtonRow(block.id, row.id)} disabled={loading} />
                                            </div>
                                        </div>
                                        {row.buttons.map((button) => (
                                            <div key={button.id} className="notification-button-grid">
                                                <input
                                                    type="text"
                                                    value={button.text}
                                                    placeholder="Текст кнопки"
                                                    onChange={(e) => updateButton(block.id, row.id, button.id, { text: e.target.value })}
                                                    disabled={loading}
                                                />
                                                <select
                                                    value={button.actionKind}
                                                    onChange={(e) => updateButton(block.id, row.id, button.id, { actionKind: e.target.value as ButtonActionKind })}
                                                    disabled={loading}
                                                >
                                                    <option value="URL">URL</option>
                                                    <option value="CALLBACK">Callback</option>
                                                </select>
                                                {button.actionKind === "URL" ? (
                                                    <input
                                                        type="text"
                                                        value={button.url || ""}
                                                        placeholder="https://..."
                                                        onChange={(e) => updateButton(block.id, row.id, button.id, { url: e.target.value })}
                                                        disabled={loading}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={button.callbackData || ""}
                                                        placeholder="callback_data"
                                                        onChange={(e) => updateButton(block.id, row.id, button.id, { callbackData: e.target.value })}
                                                        disabled={loading}
                                                    />
                                                )}
                                                <Button type="button" variant="ghost" icon={<Trash2 size={14} />} iconOnly onClick={() => removeButton(block.id, row.id, button.id)} disabled={loading || row.buttons.length === 1} />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <Button type="submit" disabled={loading || !canSubmit}>
                {loading ? "Отправка..." : "Отправить уведомления"}
            </Button>
        </form>
    );
};
